import { Users } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'
import type { PartnerView } from '@/components/Partners/partnerView.types'

interface PartnersTableProps {
  partners: PartnerView[]
  loading: boolean
  search: string
  onSelect: (partner: PartnerView) => void
  onEdit?: (partner: PartnerView) => void
  onDelete?: (id: string) => void
  onValidatePortalAccess?: (partner: PartnerView) => void
}

export function PartnersTable({
  partners,
  loading,
  search,
  onSelect,
  onEdit,
  onDelete,
  onValidatePortalAccess,
}: PartnersTableProps) {
  const hasActions = Boolean(onEdit || onDelete || onValidatePortalAccess)

  if (loading) {
    return (
      <div className='glass-card p-0 overflow-hidden'>
        <div className='p-4 space-y-3'>
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className='flex items-center gap-4 animate-pulse'>
              <div className='w-9 h-9 rounded-lg bg-[var(--glass-bg)]' />
              <div className='flex-1 h-4 rounded bg-[var(--glass-bg)]' />
              <div className='w-28 h-4 rounded bg-[var(--glass-bg)]' />
              <div className='w-12 h-5 rounded bg-[var(--glass-bg)]' />
              <div className='w-16 h-5 rounded-full bg-[var(--glass-bg)]' />
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (partners.length === 0) {
    return (
      <div className='glass-card p-12 text-center'>
        <Users size={56} className='mx-auto mb-3 text-[var(--text-muted)] opacity-50' />
        <h3 className='text-[var(--font-color-body)] font-medium'>
          {search ? 'Nenhum parceiro encontrado' : 'Nenhum parceiro cadastrado'}
        </h3>
      </div>
    )
  }

  return (
    <div className='glass-card p-0 overflow-hidden'>
      <div className='overflow-x-auto'>
        <table className='w-full'>
          <thead>
            <tr className='border-b border-[var(--glass-border)]'>
              {['Nome / Empresa', 'Contato', 'Comissão', 'Métricas', 'Status', ...(hasActions ? ['Ações'] : [])].map((h) => (
                <th
                  key={h}
                  className='text-left px-4 py-3 text-[var(--font-color-label)] font-medium uppercase tracking-wider'
                  style={{ fontSize: 'var(--font-size-label)' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {partners.map((partner) => {
              const isPendingPortal = !partner.portalUserId
              const statusClass = isPendingPortal
                ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                : partner.indicacaoAtiva
                  ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                  : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)]'
              const statusLabel = isPendingPortal ? 'Pendente' : partner.indicacaoAtiva ? 'Ativo' : 'Inativo'
              const indicacoes = partner._count?.indicacoes ?? ((partner._count?.leads ?? 0) + (partner._count?.leadInstances ?? 0))
              const negociacoes = partner._count?.deals ?? 0
              return (
                <tr
                  key={partner.id}
                  className='border-b border-[var(--glass-border)] last:border-0 hover:bg-[var(--glass-bg)] transition-colors cursor-pointer'
                  onClick={() => onSelect(partner)}
                >
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-3'>
                    <div className='w-9 h-9 rounded-lg bg-[var(--brand-primary-muted)] flex items-center justify-center shrink-0'>
                      <span className='text-[11px] font-bold text-[var(--brand-primary)]'>
                        {(partner.nome || '??').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                    <div>
                      <p className='font-medium text-[var(--font-color-body)] text-sm'>{partner.nome}</p>
                      {partner.empresa && (
                        <p className='text-[var(--font-color-muted)] text-[11px] uppercase tracking-wider'>{partner.empresa}</p>
                      )}
                      <p className='text-[var(--font-color-muted)] text-[10px]'>
                        {partner.responsavelUser?.name ? `Comercial: ${partner.responsavelUser.name}` : 'Sem comercial'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <p className='text-[var(--font-color-body)] text-[11px] font-medium'>{partner.email ?? '—'}</p>
                  <p className='text-[var(--font-color-muted)] text-[10px]'>{partner.telefone ?? '—'}</p>
                </td>
                <td className='px-4 py-3'>
                  <span className='px-2 py-0.5 rounded-md text-[9px] font-black uppercase bg-amber-500/10 text-amber-500 border border-amber-500/20'>
                    {partner.comissao}%
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <div className='flex flex-wrap items-center gap-1.5'>
                    <span
                      title='Leads e indicações enviados por este parceiro'
                      className='inline-flex items-baseline gap-1 rounded-md border border-indigo-500/20 bg-indigo-500/10 px-2 py-1 text-indigo-400'
                    >
                      <span className='text-sm font-semibold'>{indicacoes}</span>
                      <span className='text-[11px] font-medium'>{indicacoes === 1 ? 'indicação' : 'indicações'}</span>
                    </span>
                    <span
                      title='Negociações abertas com clientes deste parceiro'
                      className='inline-flex items-baseline gap-1 rounded-md border border-teal-500/20 bg-teal-500/10 px-2 py-1 text-teal-400'
                    >
                      <span className='text-sm font-semibold'>{negociacoes}</span>
                      <span className='text-[11px] font-medium'>{negociacoes === 1 ? 'negociação' : 'negociações'}</span>
                    </span>
                  </div>
                </td>
                  <td className='px-4 py-3'>
                    <span className={cn('px-2 py-0.5 rounded-md text-[8px] font-bold uppercase', statusClass)}>
                      {statusLabel}
                    </span>
                  </td>
                  {hasActions && (
                    <td className='px-4 py-3'>
                    <div className='flex items-center gap-1'>
                      {isPendingPortal && onValidatePortalAccess && (
                        <Button
                          variant='primary'
                          size='sm'
                          onClick={(e) => { e.stopPropagation(); onValidatePortalAccess(partner) }}
                        >
                          Validar
                        </Button>
                      )}
                      {onEdit && (
                        <Button variant='ghost' size='sm' onClick={(e) => { e.stopPropagation(); onEdit(partner) }}>
                          Editar
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant='ghost'
                          size='sm'
                          className='text-[#e5484d] hover:text-[#e5484d]'
                          onClick={(e) => { e.stopPropagation(); onDelete(partner.id) }}
                        >
                          Excluir
                        </Button>
                      )}
                    </div>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
